/**
 * Definitions panel — always present, collapsible. States the handoff
 * decisions (requirements §4.6) in plain language on the report itself.
 * Content lives in definitions.ts so both reports share one source.
 */
import { ChevronDown, ChevronRight, Info } from "lucide-react";
import { useState } from "react";

import type { Definition } from "./definitions";

interface Props {
  items: Definition[];
  /** Short right-aligned summary on the collapsed bar, e.g. the rates in effect. */
  summary?: string;
}

export function DefinitionsPanel({ items, summary }: Props) {
  const [open, setOpen] = useState(false);
  return (
    <section className="rounded-lg border hairline mb-6" style={{ background: "var(--bg-elev)" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center gap-2 px-5 py-3 text-left font-serif text-[14px] t-base focus-ring rounded-lg"
        style={{ color: "var(--ink)" }}
      >
        {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        <Info size={14} style={{ color: "var(--ink-3)" }} />
        How these numbers are calculated
        {summary && (
          <span className="ml-auto font-mono text-[12px] font-normal" style={{ color: "var(--ink-3)" }}>
            {summary}
          </span>
        )}
      </button>
      {open && (
        <dl className="px-5 pb-4 grid gap-3 text-[13.5px]" style={{ color: "var(--ink-2)" }}>
          {items.map((it) => (
            <div key={it.term}>
              <dt className="font-serif" style={{ color: "var(--ink)" }}>
                {it.term}
              </dt>
              <dd className="mt-0.5 leading-relaxed">{it.body}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}
